import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Form } from "Components/Core";
import {
  HeaderComponent,
  PaginationControl,
  SearchControl,
  TableComponent,
} from "Components/Shared";
import ReassignCoach from "Components/Shared/ReassignCoach";
import {
  getAssetReviewRequests,
  reassignReviewRequest,
  patchMarkBulkPayment,
} from "Api/Pages/AssetReviewRequestsApi";
import {
  AssetReviewRequest,
  GetAssetReviewRequestsParams,
} from "Interfaces/Pages/AssetReviewRequestsInterface";
import { INITIAL_PAGE, RECORDS_PER_PAGE } from "App/constants";
import {
  getAssetReviewRequestColumns,
  ASSET_REVIEW_REQUESTS_TITLE,
} from "./constants";
import { STATUS_TABS } from "./constants";

type AssetReviewRequestsOriginState = {
  __origin?: {
    currentPage?: number;
    status?: GetAssetReviewRequestsParams["status"];
    isBulkMode?: boolean;
    selectedIds?: number[];
  };
};

const AssetReviewRequests = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const origin = (location.state as AssetReviewRequestsOriginState | null)
    ?.__origin;
  const [currentPage, setCurrentPage] = useState<number>(
    origin?.currentPage ?? INITIAL_PAGE
  );
  const [totalPages, setTotalPages] = useState<number>(INITIAL_PAGE);
  const [searchInput, setSearchInput] = useState<string>("");
  const [status, setStatus] = useState<GetAssetReviewRequestsParams["status"]>(
    origin?.status ?? "pending"
  );
  const [isBulkMode, setIsBulkMode] = useState<boolean>(
    origin?.isBulkMode ?? false
  );
  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    new Set(origin?.selectedIds || [])
  );
  const [reassignCoach, setReassignCoach] = useState<{
    show: boolean;
    reviewRequestId: number;
    masterSportId: number;
    currentCoachId?: number;
  }>({
    show: false,
    reviewRequestId: 0,
    masterSportId: 0,
  });

  const { data, refetch } = useQuery(
    ["asset-review-requests", currentPage, searchInput, status],
    async () =>
      getAssetReviewRequests({
        page: currentPage,
        perPage: RECORDS_PER_PAGE,
        search: searchInput,
        status: status,
        order_by: "created_at",
        order_type: status === "cancelled" ? "DESC" : "ASC",
        unpaid_first: status === "completed" ? true : undefined,
      }),
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );

  const reassignCoachMutation = useMutation(
    ({
      reviewRequestId,
      coachId,
    }: {
      reviewRequestId: number;
      coachId: number;
    }) => reassignReviewRequest(reviewRequestId, coachId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["asset-review-requests"]);
        setReassignCoach({ show: false, reviewRequestId: 0, masterSportId: 0 });
      },
      onError: (error) => {
        console.error("Error reassigning coach:", error);
      },
    }
  );

  const bulkPaymentMutation = useMutation(
    async ({ coachPaid }: { coachPaid: boolean }) =>
      patchMarkBulkPayment(Array.from(selectedIds), coachPaid),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["asset-review-requests"]);
        // clear selection and exit bulk mode after successful update
        setSelectedIds(new Set());
        setIsBulkMode(false);
      },
    }
  );

  type AugmentedRow = AssetReviewRequest & { _bulk?: { checked: boolean } };
  const tableData = useMemo<AssetReviewRequest[]>(
    () => data?.data || [],
    [data]
  );

  useEffect(() => {
    if (data?.total_count !== undefined) {
      setTotalPages(Math.ceil(data.total_count / RECORDS_PER_PAGE));
    }
  }, [data?.total_count]);

  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setSearchInput(value);
      setCurrentPage(INITIAL_PAGE);
    },
    []
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      refetch();
    },
    [refetch]
  );

  const handleStatusChange = useCallback(
    (newStatus: GetAssetReviewRequestsParams["status"]) => {
      setStatus(newStatus);
      setCurrentPage(INITIAL_PAGE);
      setIsBulkMode(false);
      setSelectedIds(new Set());
    },
    []
  );

  const handleReassignCoach = useCallback(
    (
      reviewRequestId: number,
      masterSportId: number,
      currentCoachId?: number
    ) => {
      setReassignCoach({
        show: true,
        reviewRequestId,
        masterSportId,
        currentCoachId,
      });
    },
    []
  );

  const handleReassignCoachClose = useCallback(() => {
    setReassignCoach({ show: false, reviewRequestId: 0, masterSportId: 0 });
  }, []);

  const handleReassignCoachConfirm = useCallback(
    (coachId: number) => {
      reassignCoachMutation.mutate({
        reviewRequestId: reassignCoach.reviewRequestId,
        coachId,
      });
    },
    [reassignCoach.reviewRequestId, reassignCoachMutation]
  );

  const toggleBulkMode = () => {
    if (!isBulkMode) {
      setSelectedIds(new Set());
    }
    setIsBulkMode(!isBulkMode);
  };

  const handleRowCheckbox = (id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const markPaid = (paid: boolean) => {
    if (!selectedIds.size) return;
    bulkPaymentMutation.mutate({ coachPaid: paid });
  };

  return (
    <div className="ss-page-content d-flex flex-column flex-1 scroll-y bg-white">
      <HeaderComponent
        title={ASSET_REVIEW_REQUESTS_TITLE}
        shouldShowNav={false}
      />
      <div className="ss-page-body d-flex flex-column flex-1 scroll-y py-4">
        <div className="d-flex align-items-center justify-content-between mb-4 px-4">
          <div className="d-flex gap-2">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                className={`btn btn-outline-secondary ${
                  status === tab.value ? "active" : ""
                }`}
                onClick={() => handleStatusChange(tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="d-flex align-items-center gap-2">
            {status === "completed" && !isBulkMode && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={toggleBulkMode}
              >
                Mark Payment
              </button>
            )}
            {status === "completed" && isBulkMode && (
              <>
                <button
                  type="button"
                  className="btn btn-outline-success"
                  onClick={() => markPaid(true)}
                >
                  Mark Paid
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() => markPaid(false)}
                >
                  Mark Unpaid
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={toggleBulkMode}
                >
                  Cancel
                </button>
              </>
            )}
            <Form className="d-flex" onSubmit={handleSubmit}>
              <SearchControl
                className="mt-0"
                placeholder="Search"
                label="Search"
                onChange={handleSearch}
              />
            </Form>
          </div>
        </div>
        <div className="flex-1 scroll-y px-4">
          <div className="table-responsive">
            <TableComponent
              columns={getAssetReviewRequestColumns({
                status,
                onReassignCoach: handleReassignCoach,
                isBulkMode: status === "completed" && isBulkMode,
                onToggleRow: handleRowCheckbox,
              })}
              tableData={
                isBulkMode
                  ? (tableData as AssetReviewRequest[]).map(
                      (row): AugmentedRow => ({
                        ...row,
                        _bulk: {
                          checked: selectedIds.has(row.id),
                        },
                      })
                    )
                  : tableData
              }
              isClickAble
              onRowClick={(row: AssetReviewRequest) => {
                navigate(`/review-requests/${row.id}`, {
                  state: {
                    ...row,
                    __origin: {
                      currentPage,
                      status,
                      isBulkMode,
                      selectedIds: Array.from(selectedIds),
                    },
                  },
                });
              }}
            />
          </div>
        </div>
      </div>
      <div className="pagination-wrap d-flex justify-content-end px-4">
        <PaginationControl
          currentPage={currentPage}
          setCurrentPage={(current: number) => setCurrentPage(current)}
          totalPages={totalPages}
        />
      </div>

      <ReassignCoach
        show={reassignCoach.show}
        onClose={handleReassignCoachClose}
        onReassign={handleReassignCoachConfirm}
        masterSportId={reassignCoach.masterSportId}
        currentCoachId={reassignCoach.currentCoachId}
      />
    </div>
  );
};

export default AssetReviewRequests;
